import dayjs from 'dayjs';
import React, {useMemo, useState} from 'react';
import {Stack, Box} from '@mui/material';
import {
  Body,
  designSystemToken,
  Headline,
  Icon,
  Label,
} from '@lightricks/react-design-system';
import ActionsMenu from '@contentlab/components/actions-menu';
import {MenuOption} from '@contentlab/components/actions-menu/ActionsMenu';
import HoverableVideo from '@contentlab/components/hoverable-video';
import ProjectProgressBadge from '@contentlab/components/project-progress-badge';
import ReadyAssetsState from '@contentlab/components/ready-assets-state';
import useLiveEstimatedMinutes from '@contentlab/hooks/use-live-estimated-minutes';
import ProjectAction from '@contentlab/types/ProjectAction';
import {FeatureName, ProjectStatus} from '@contentlab/types/project';
import {ProjectProps, ThumbnailData} from '@contentlab/types/projectProps';
import getFeatureIconName from '@contentlab/utils/getFeatureIconName';
import translate from '@/utils/translate';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import GradientProgressBar from '@/components/gradient-progress-bar';
import useOnHover from '@/hooks/use-on-hover';
import {
  AssetContainer,
  ActionsCell,
  Boxes,
  CardContainer,
  CardSpacer,
  FeatureIconContainer,
  FooterContainer,
  HeaderContainer,
  ProgressContainer,
  ProgressInfo,
  RootCard,
  StackedBox,
  UserInputPill,
  UserInputsRow,
} from './ProjectCard.styles';
import {IMAGE_HEIGHT, IMAGE_WIDTH} from './constants';

interface ProjectCardProps {
  testID?: string;
  project: ProjectProps;
  onActionsClick: (project: ProjectProps) => void;
  onProjectActionClick: (action: ProjectAction, project: ProjectProps) => void;
}

const FEATURE_ICON_STYLE = {width: '17.33px', height: '17.33px'};

const FEATURE_STYLE = () => ({
  [FeatureName.AI_DUBBING]: {
    icon: (
      <Icon
        size="medium"
        appearance="white"
        name={getFeatureIconName(FeatureName.AI_DUBBING)}
        style={FEATURE_ICON_STYLE}
      />
    ),
  },
  [FeatureName.REFORMATTING]: {
    icon: (
      <Icon
        size="medium"
        appearance="white"
        name={getFeatureIconName(FeatureName.REFORMATTING)}
        style={FEATURE_ICON_STYLE}
      />
    ),
  },
  [FeatureName.REPHRASING]: {
    icon: (
      <Icon
        size="medium"
        appearance="white"
        name={getFeatureIconName(FeatureName.REPHRASING)}
        style={FEATURE_ICON_STYLE}
      />
    ),
  },
  [FeatureName.CAPTIONS]: {
    icon: (
      <Icon
        size="medium"
        appearance="white"
        name={getFeatureIconName(FeatureName.CAPTIONS)}
        style={FEATURE_ICON_STYLE}
      />
    ),
  },
  [FeatureName.AI_AVATAR]: {
    icon: (
      <Icon
        size="medium"
        appearance="white"
        name={getFeatureIconName(FeatureName.AI_AVATAR)}
        style={FEATURE_ICON_STYLE}
      />
    ),
  },
  [FeatureName.UPSCALE]: {
    icon: (
      <Icon
        size="medium"
        appearance="white"
        name={getFeatureIconName(FeatureName.UPSCALE)}
        style={FEATURE_ICON_STYLE}
      />
    ),
  },
});

function InteractiveAsset({
  asset,
  isHovered,
}: {
  asset: ThumbnailData | null;
  isHovered: boolean;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  if (!asset) {
    return null;
  }

  return (
    <Stack paddingX="16px" width="100%" height="100%">
      <Boxes>
        <StackedBox rotation="-7.5deg" />
        <StackedBox rotation="7deg" />
        {imageFailed || !asset.thumbnailUrl ? (
          <StackedBox rotation="0deg" opacity={1} />
        ) : (
          <StackedBox rotation="0deg" opacity={1}>
            <HoverableVideo
              videoUrl={asset.videoUrl}
              thumbnailUrl={asset.thumbnailUrl}
              width={IMAGE_WIDTH}
              height={IMAGE_HEIGHT}
              isHovered={isHovered}
              preloadVideo={false}
              sx={{borderRadius: '6.528px'}}
              onImageError={() => setImageFailed(true)}
            />
          </StackedBox>
        )}
      </Boxes>
    </Stack>
  );
}

function UserInputs({
  background,
  color,
  params,
}: {
  background: string;
  color: string;
  params: string[];
}) {
  return (
    <UserInputsRow>
      {params.map((param) => (
        <UserInputPill key={param} background={background}>
          <Label size="md" color={color} sx={{padding: '3px 6px'}}>
            {param}
          </Label>
        </UserInputPill>
      ))}
    </UserInputsRow>
  );
}

function ProjectCard({
  testID = 'project-card',
  project,
  onActionsClick,
  onProjectActionClick,
}: ProjectCardProps) {
  const [isHovered, {onMouseEnter, onMouseLeave}] = useOnHover();
  const displayAsset = useMemo(() => {
    return project.thumbnails[0] || null;
  }, [project.thumbnails]);

  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] =
    useState<HTMLElement | null>(null);

  const handleOnActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    onActionsClick(project);
    setActionsMenuAnchorEl(event.currentTarget);
  };

  const handleOnActionsClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setActionsMenuAnchorEl(null);
  };

  const {remainingMinutes, progressPercentage} = useLiveEstimatedMinutes(
    project.estimatedCompletionTime,
    project.createdAt
  );

  const featureStyle = useMemo(
    () => FEATURE_STYLE()[project.feature],
    [project.feature]
  );

  const isCompleted = [ProjectStatus.READY, ProjectStatus.FAILED].includes(
    project.status
  );

  const menuOptions: MenuOption[] = [
    {
      id: ProjectAction.Rename,
      title: translate('Rename'),
      icon: 'Navigation-Edit',
      onClick: () => {
        onProjectActionClick(ProjectAction.Rename, project);
      },
    },
    {
      id: ProjectAction.Download,
      title: translate('Download'),
      icon: 'Navigation-Download',
      onClick: async () => {
        onProjectActionClick(ProjectAction.Download, project);
      },
    },
    {
      id: ProjectAction.Delete,
      title: translate('Delete'),
      icon: 'Actions-Remove',
      onClick: () => {
        onProjectActionClick(ProjectAction.Delete, project);
      },
    },
  ];

  return (
    <RootCard
      data-testid={testID}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <CardContainer>
        <HeaderContainer>
          <AssetContainer>
            <InteractiveAsset asset={displayAsset} isHovered={isHovered} />
          </AssetContainer>

          {project.status === ProjectStatus.READY ? (
            <ActionsCell key="actions">
              <Box onClick={handleOnActionsClick}>
                <Icon size="medium" appearance="neutral" name="Actions-More" />
              </Box>
              <ActionsMenu
                menuOpen={!!actionsMenuAnchorEl}
                onCloseMenu={handleOnActionsClose}
                anchorEl={actionsMenuAnchorEl}
                menuOptions={menuOptions}
              />
            </ActionsCell>
          ) : (
            <ProjectProgressBadge
              remainingMinutes={remainingMinutes}
              status={project.status}
              showIcon={false}
            />
          )}
        </HeaderContainer>
        <CardSpacer />
        <ProgressContainer>
          <ProgressInfo>
            {isHovered ? (
              <UserInputs
                background={designSystemToken('semantic.bg.brand-secondary')}
                color={designSystemToken('semantic.fg.brand')}
                params={project.userInputs.slice(0, 3)}
              />
            ) : (
              <Body
                size="lg"
                color={designSystemToken('semantic.fg.primary')}
                noWrap
              >
                {project.name}
              </Body>
            )}

            {isHovered &&
            typeof progressPercentage === 'number' &&
            !isCompleted ? (
              <Label size="lg">{progressPercentage.toFixed()}%</Label>
            ) : (
              <ReadyAssetsState
                completed={project.state.readyCount}
                total={project.state.totalFlows}
              />
            )}
          </ProgressInfo>
          <GradientProgressBar
            style={{width: '100%', opacity: isCompleted ? 0 : 1}}
            initialValue={isCompleted ? 100 : progressPercentage || 0}
            value={isCompleted ? 100 : progressPercentage || 0}
            backgroundColor={designSystemToken('semantic.bg.tertiary')}
            fillColor={
              isHovered
                ? designSystemToken('semantic.fg.primary')
                : designSystemToken('semantic.bg.neutral-secondary')
            }
          />
        </ProgressContainer>
      </CardContainer>
      <FooterContainer>
        <FeatureIconContainer
          background={designSystemToken('semantic.fg.primary')}
        >
          {featureStyle.icon}
        </FeatureIconContainer>
        <Headline
          size="xs"
          color={designSystemToken('semantic.fg.primary')}
          sx={{flex: 1}}
        >
          {translate(project.featureName)}
        </Headline>
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {dayjs(project.createdAt).format(MONTH_SHORT_DAY_YEAR_FORMAT)}
        </Body>
      </FooterContainer>
    </RootCard>
  );
}

export default ProjectCard;
